/**
 * §F9 "Preview on map" — **the card's own layer, on its own theme** (client item 9).
 *
 * > *"Preview on map should show the layer of that card, on that card's theme — not
 * > whatever was already on the map."*
 *
 * The old handler was two lines and both were wrong. It turned on `layer_ids[0]`, which is
 * *primary key order*: a dataset whose raster sorts after its point layer previewed as
 * points while the thumbnail beside it drew the raster. And it touched no theme at all, so
 * the explorer kept whichever rail chip the reader had left selected — a water card opening
 * onto the soil theme's panel, and the eleven layerless §F8 rows (MELS values, the ten 2025
 * baseline sheets) opening onto nothing whatever.
 *
 * Both answers are now the **server's**, one per row: `primary_layer_id` and `preview`
 * (`apps.datasets.catalogue.primary_layer`/`preview_for`). The card asks for neither and
 * guesses at neither, which is what makes two readers clicking the same card land on the
 * same map.
 *
 * ## Why this is not a permalink
 *
 * Everything else that moves the explorer from elsewhere writes a hash. A theme cannot:
 * `domFilter` is **not** a permalink key (`lib/permalink.ts` — the hash carries `ly`, `v`,
 * `ind` and the geography, and `v` is the *view*, not the rail's pick). So the preview does
 * what the rail's chip does — write the store, then navigate — and determinism comes from
 * the server having chosen the layer and named the theme, not from a URL.
 *
 * ## Why the theme is selected first
 *
 * Selecting a theme applies that theme's **default dataset** (`applyThemeDefault`), which
 * clears the mapped variable and swaps the active raster. Turning the card's layer on first
 * would hand it straight to that clearance. So: theme, then layer — and the layer goes on
 * the way the panes turn one on, `setActiveRaster` for a raster and `toggleLayer` for a
 * vector, because R4's no-raster-over-raster rule *refuses* a second raster and a bare
 * `toggleLayer` would have been silently dropped on exactly the rasters this page is full of.
 */
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  fetchConfig,
  fetchIndicators,
  fetchLayers,
  queryKeys,
  type DatasetPreview,
  type Layer,
} from '../../lib/api';
import { useAppStore } from '../../lib/state';
import { toast } from '../../lib/toast';
import { isRasterKind } from '../map/layerKinds';
import { selectTheme } from '../sidebar/applyTheme';
import { INDICATORS_THEME } from '../sidebar/themes';

/** What the two callers have in common — the list row and the record both carry it. */
export interface Previewable {
  preview: DatasetPreview | null;
}

/**
 * The card's and the record's one "Preview on map" handler.
 *
 * Shared deliberately: the same dataset is previewed from two controls, and two
 * implementations would be two answers to "which layer is this card about" — the bug
 * client item 9 reports, re-created one modal away.
 */
export function useDatasetPreview(): (row: Previewable) => void {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = useAppStore((s) => s.role);
  // The three catalogues `applyThemeDefault` reads. Already cached by the explorer in any
  // session that has been there; a cold catalogue-first visit fetches them once, and a
  // theme whose table has not landed simply applies no default — never a half-loaded one.
  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });
  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });

  return (row: Previewable) => {
    const preview = row.preview;
    // No layer, no indicators, no sheet: a table-only record has nowhere to be previewed,
    // and says so rather than navigating to an unchanged map.
    if (!preview) {
      toast(t('catalogue.record.tabularPreview'));
      return;
    }
    const catalogues = { config, layers, indicators };
    if (preview.kind === 'layer' && preview.layer_id) {
      // A dataset with no domain has no theme to land on; the layer still goes on.
      if (preview.theme) selectTheme(preview.theme, catalogues, t);
      showLayer(preview.layer_id, layers);
    } else if (preview.kind === 'indicators') {
      selectTheme(INDICATORS_THEME, catalogues, t);
    } else if (preview.kind === 'baseline' && preview.theme) {
      // A `bl_<sheet>` key is a theme like any other — `isBaselineTheme` routes the panel
      // and the rail's accordion opens itself on arrival at one.
      selectTheme(preview.theme, catalogues, t);
    }
    navigate('/');
  };
}

/**
 * Turn one layer on, by the same rule the §F5.2 pane uses.
 *
 * `toggleLayer` is the vector control and R4 makes it *refuse* a raster while another
 * raster is on — which is precisely the state `selectTheme` just left the store in when the
 * theme's default is a raster. `setActiveRaster` is the raster control: it drops the other
 * rasters and puts this one in, atomically, so the card's own layer is the one that survives.
 *
 * With the catalogue still loading nothing is known to be a raster and the layer is simply
 * added — the same deliberate failure `state.ts`'s raster register documents, and
 * `registerRasterLayers` normalises the set the moment the rule becomes evaluable.
 */
function showLayer(layerId: string, layers: Layer[] | undefined): void {
  const store = useAppStore.getState();
  const rasterIds = (layers ?? []).filter((l) => isRasterKind(l.kind)).map((l) => l.id);
  if (rasterIds.includes(layerId)) store.setActiveRaster(layerId, rasterIds);
  else store.toggleLayer(layerId, true);
}
